import React from 'react'
import { capitalize, startCase } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Box, DataListItem, RowsSkeletonLoader } from '@node-space/ui-components'
import { RequestErrorStatusCard } from 'components/Errors/RequestErrorStatusCard'
import { SidePanelSection } from 'components/SidePanel/SidePanelSection'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useGetCustomerDocumentsV2 } from 'hooks/queries/Customers/useCustomerDocumentsV2Query'
import { CustomerDocumentStatusHighlight } from 'pages/Customers/components/CustomerHighlight'
import { CustomerDocumentStatus } from 'types/customers'
import { DOCUMENT_TYPES } from '../helpers/constants'

type DocumentTypeKey = (typeof DOCUMENT_TYPES)[number]

interface CustomerDocumentsTabProps {
  pendingDocumentCodes: Set<string>
  customerReference: string
}

export const CustomerDocumentsTab = ({
  pendingDocumentCodes,
  customerReference,
}: CustomerDocumentsTabProps) => {
  const { t } = useTranslation()

  const isPending = (docKey: DocumentTypeKey) => pendingDocumentCodes.has(docKey)
  const {
    data: documentsData,
    isLoading: isLoadingDocuments,
    isError: isDocumentsError,
    refetch,
  } = useGetCustomerDocumentsV2(customerReference)

  if (isLoadingDocuments) {
    return <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />
  }

  if (isDocumentsError) {
    return (
      <Box paddingX={16} paddingY={32}>
        <RequestErrorStatusCard onReload={refetch} />
      </Box>
    )
  }

  const uploadedDocuments =
    documentsData?.content?.filter(
      document => document?.status === CustomerDocumentStatus.COMPLETE
    ) ?? []

  const requiredDocuments = DOCUMENT_TYPES.filter(isPending)

  return (
    <Box className="flex flex-col gap-2" padding={16}>
      <SidePanelSection heading={t('embeddedCustomers.documentsSection.requiredDocuments')}>
        {requiredDocuments.map((docKey, idx) => {
          const label = t(`embeddedCustomers.requiredDocuments.${docKey}`)
          return (
            <DataListItem
              key={docKey}
              layout="horizontal"
              body=""
              title={`${idx + 1}. ${startCase(capitalize(label))}`}
              id={docKey}
              testId="customer-document"
              HeaderHighlight={
                <CustomerDocumentStatusHighlight status="PENDING" isUploaded={false} />
              }
            />
          )
        })}
      </SidePanelSection>

      {uploadedDocuments?.length > 0 && (
        <SidePanelSection heading={t('embeddedCustomers.documentsSection.uploadedDocuments')}>
          {uploadedDocuments.map((doc, idx) => (
            <DataListItem
              key={doc?.id}
              layout="horizontal"
              body=""
              title={`${idx + 1}. ${doc?.filename}`}
              id={idx}
              testId="customer-document"
            />
          ))}
        </SidePanelSection>
      )}
    </Box>
  )
}
